import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Skeleton, Typography } from "@mui/material";

import { useCustomNavigate } from "../../provider/navigate";
import { useI18n } from "react-simple-i18n";

interface CourseCardProps {
	type: string
	data: CourseObject;
}

const CourseCard = ({ data, type }: CourseCardProps) => {
	const { t } = useI18n();
	const { customNavigate } = useCustomNavigate();
	const videoRef = React.useRef<HTMLVideoElement>(null);

	const handleMouseEnter = () => {
		try {
			if (!videoRef.current) return;
			videoRef.current.play().catch(() => { });
		} catch (err: any) {
			console.log("handleMouseEnter::", err.message);
		}
	}

	const handleMouseLeave = () => {
		try {
			if (!videoRef.current || !videoRef.current.pause) return;
			videoRef.current.pause();
			videoRef.current.currentTime = 0;
		} catch (err: any) {
			console.log("handleMouseLeave::", err.message);
		}
	}

	return (
		<CourseCardWrapper onMouseEnter={handleMouseEnter}
			onClick={customNavigate(`/course/${data.id}`)}
			onMouseLeave={handleMouseLeave}
		>
			{!!type && (
				<Typography variant="body2" component="h5">
					{t(`header.${type}`)}
				</Typography>
			)}

			<VideoContainer>
				<video ref={videoRef}
					muted preload="auto"
					src={data.lectureVideo}
					disablePictureInPicture
					onAbort={() => console.log("Video loading was aborted")}
					onError={(e: any) => console.log("Error loading video:", e.message)}
				/>
			</VideoContainer>

			<CourseContent>
				<Typography variant="h6">
					{data.title}
				</Typography>

				<Typography variant="body2">
					{data.description}
				</Typography>

				<Typography variant="body1" component="span">
					${data.price}
				</Typography>
			</CourseContent>
		</CourseCardWrapper>
	)
}

const CourseCardSkeleton = () => {
	return (
		<CourseCardWrapper>
			<VideoContainer>
				<Skeleton variant="rectangular" width="100%" height="100%" />
			</VideoContainer>

			<CourseContent>
				<Skeleton variant="text" height={35} width="50%" />
				<Skeleton variant="text" height={25} />
				<Skeleton variant="text" height={25} width={50} />
			</CourseContent>
		</CourseCardWrapper>
	)
}

const CourseCardWrapper = styled(Box)(({ theme }) => ({
	maxWidth: 350,
	margin: "0 auto",
	position: "relative",
	borderRadius: theme.spacing(1),
	backgroundColor: theme.palette.common.mainBg,
	boxShadow: `0px 1px 5px ${theme.palette.common.firstBorder}`,
	transition: "transform 0.2s, box-shadow 0.2s",

	height: "100%",
	overflow: "hidden",
	cursor: "pointer",


	h5: {
		zIndex: 2,
		top: 0, left: 0,
		position: "absolute",
		display: "inline-block",
		padding: theme.spacing(0.5, 2),
		borderRadius: `0 0 ${theme.spacing(1)} 0`,
		backgroundColor: `${theme.palette.common.thirdColor}80`,

		fontWeight: 500,
		textTransform: "capitalize",
		color: theme.palette.common.secondColor,
	},

	"&:hover": {
		transform: "translateY(-4px)",
	},
}))

const VideoContainer = styled(Box)({
	width: "100%",
	position: "relative",
	paddingTop: "56.25%", // 16:9 Aspect Ratio

	"& video, .MuiSkeleton-root": {
		top: 0, left: 0,
		position: "absolute",
		width: "100%",
		height: "100%",
		objectFit: "cover",
	}
})

const CourseContent = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(0.7),
	padding: theme.spacing(1.5),

	h6: {
		fontWeight: 600,
		WebkitLineClamp: 2,
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		lineHeight: 1.2,
		overflow: "hidden",
	},

	p: {
		flex: 1,
		WebkitLineClamp: 1,
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		color: `${theme.palette.common.firstColor}c0`,
		overflow: "hidden",
	},

	span: {
		fontWeight: 600,
		marginTop: "auto",
	}
}))

export { CourseCard, CourseCardSkeleton };